import React, { useEffect, useRef, useState } from 'react';
import OSDForm from './OSDManifestForm';
import { DateFormats } from '@lanterds/types';
import dayjs from 'dayjs';
import { getOsdManifestReportPdf } from 'src/api/reports';
import { Button } from '@mui/material';
import './OSDManifest.scss';
import { SnackbarKey, useSnackbar } from 'notistack';
import { openPDFInNewTab } from 'src/util/openPdf';

export default function OSDManifest() {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const [isGeneratingReport, setIsGeneratingReport] = useState(false);
    const [selectedRoute, setSelectedRoute] = useState<string | null>(null);
    const snackbarRef = useRef<SnackbarKey | null>(null);

    useEffect(() => {
        return () => {
            if (snackbarRef.current) {
                closeSnackbar(snackbarRef.current);
                snackbarRef.current = null;
            }
        };
    }, [closeSnackbar]);

    const generateReport = async () => {
        if (!selectedRoute) {
            enqueueSnackbar('Please select a route before generating the report.', {
                variant: 'warning',
                preventDuplicate: true,
            });
            return;
        }

        setIsGeneratingReport(true);

        snackbarRef.current = enqueueSnackbar('Generating OS&D Manifest...', {
            variant: 'loading',
            preventDuplicate: true,
            disableWindowBlurListener: true,
            autoHideDuration: null,
            action: (key) => (
                <Button size="small" className="dismiss-button" onClick={() => closeSnackbar(key)}>
                    Dismiss
                </Button>
            ),
        });

        try {
            const response = await getOsdManifestReportPdf(dayjs().format(DateFormats.IsoDate), selectedRoute);

            if (response) {
                const { pdfBase64, error } = response;

                if (pdfBase64) {
                    openPDFInNewTab(response.pdfBase64);
                    enqueueSnackbar('OS&D Manifest PDF ready.', { variant: 'success', preventDuplicate: true });
                } else if (error) {
                    enqueueSnackbar(error, { variant: 'error', preventDuplicate: true });
                } else {
                    enqueueSnackbar('No PDF returned', { variant: 'error', preventDuplicate: true });
                }
            }
        } catch (err) {
            console.error(err);
            enqueueSnackbar('Failed to generate the OS&D Manifest PDF.', { variant: 'error', preventDuplicate: true });
        } finally {
            if (snackbarRef.current) {
                closeSnackbar(snackbarRef.current);
                snackbarRef.current = null;
            }
            setIsGeneratingReport(false);
        }
    };

    return (
        <OSDForm
            handleGenerateReport={generateReport}
            selectedRoute={selectedRoute}
            setSelectedRoute={setSelectedRoute}
            isGeneratingReport={isGeneratingReport}
        />
    );
}
